import { useEffect, useRef, useState } from "react"
import type { PointerEvent, ReactNode } from "react"

interface LoupePoint {
  x: number
  y: number
}

interface Props {
  /** When false the loupe is inert (e.g. grid thumbnails). */
  active?: boolean
  width: number
  height: number
  zoom?: number
  renderContent: (width: number, height: number, isLoupe: boolean) => ReactNode
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

/**
 * Ambient magnifier. On desktop the loupe follows the cursor (centered on it);
 * on touch it follows the finger but is offset to the upper-left so the finger
 * never covers the magnified view, flipping toward the lower-right near an
 * edge. Fades in on enter, out on leave. No click required.
 */
const ArtworkLoupe = ({ active = false, width, height, zoom = 3, renderContent }: Props) => {
  const [point, setPoint] = useState<LoupePoint | null>(null)
  const [isTouch, setIsTouch] = useState(false)
  const [visible, setVisible] = useState(false)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingPoint = useRef<LoupePoint | null>(null)
  const pendingTouch = useRef(false)
  const moveRaf = useRef<number | null>(null)

  const safeWidth = Math.max(1, width)
  const safeHeight = Math.max(1, height)
  const safeZoom = Math.max(1, zoom)
  const loupeSize = Math.round(clamp(Math.min(safeWidth, safeHeight) * 0.5, 132, 240))
  const loupeWidth = safeWidth * safeZoom
  const loupeHeight = safeHeight * safeZoom

  const cancelMoveRaf = () => {
    if (moveRaf.current == null) return
    cancelAnimationFrame(moveRaf.current)
    moveRaf.current = null
  }

  useEffect(() => {
    if (!active) {
      cancelMoveRaf()
      pendingPoint.current = null
      setVisible(false)
      setPoint(null)
    }
  }, [active])

  useEffect(() => () => {
    if (hideTimer.current) clearTimeout(hideTimer.current)
    cancelMoveRaf()
  }, [])

  const readPoint = (event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null
    return { x: (x / rect.width) * safeWidth, y: (y / rect.height) * safeHeight }
  }

  const commitPoint = (next: LoupePoint, isTouchPointer: boolean) => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current)
      hideTimer.current = null
    }
    setIsTouch(isTouchPointer)
    setPoint(next)
    setVisible(true)
  }

  const show = (event: PointerEvent<HTMLDivElement>) => {
    if (!active) return
    const next = readPoint(event)
    if (!next) {
      setVisible(false)
      return
    }
    commitPoint(next, event.pointerType !== "mouse")
  }

  const move = (event: PointerEvent<HTMLDivElement>) => {
    if (!active) return
    const next = readPoint(event)
    if (!next) {
      setVisible(false)
      return
    }
    pendingPoint.current = next
    pendingTouch.current = event.pointerType !== "mouse"
    if (moveRaf.current != null) return
    moveRaf.current = requestAnimationFrame(() => {
      moveRaf.current = null
      const queued = pendingPoint.current
      if (!queued) return
      commitPoint(queued, pendingTouch.current)
    })
  }

  const hide = () => {
    cancelMoveRaf()
    pendingPoint.current = null
    setVisible(false)
    if (hideTimer.current) clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => setPoint(null), 220)
  }

  // Where to draw the lens. Mouse: centered on the cursor. Touch: offset to the
  // upper-left of the finger, flipping to lower-right near the top/left edge.
  let lensX = point?.x ?? 0
  let lensY = point?.y ?? 0
  if (point && isTouch) {
    const offset = clamp(loupeSize * 0.62 + 36, 120, 200)
    lensX = point.x - offset
    lensY = point.y - offset
    if (lensX - loupeSize / 2 < 4) lensX = point.x + offset
    if (lensY - loupeSize / 2 < 4) lensY = point.y + offset
    lensX = clamp(lensX, loupeSize / 2, safeWidth - loupeSize / 2)
    lensY = clamp(lensY, loupeSize / 2, safeHeight - loupeSize / 2)
  }

  return (
    <div
      className="gallery-token-loupe"
      data-loupe-active={active ? "true" : "false"}
      data-loupe-visible={visible && point ? "true" : "false"}
      style={{ width: safeWidth, height: safeHeight }}
      onPointerEnter={show}
      onPointerDown={show}
      onPointerMove={move}
      onPointerLeave={hide}
      onPointerCancel={hide}
      onPointerUp={(event) => {
        if (event.pointerType !== "mouse") hide()
      }}
    >
      <div className="gallery-token-loupe-base">{renderContent(safeWidth, safeHeight, false)}</div>

      {point && (
        <div
          className="gallery-token-loupe-lens"
          aria-hidden="true"
          style={{
            width: loupeSize,
            height: loupeSize,
            left: lensX,
            top: lensY,
            opacity: visible ? 1 : 0,
          }}
        >
          <div
            className="gallery-token-loupe-lens-content"
            style={{
              width: loupeWidth,
              height: loupeHeight,
              transform: `translate3d(${loupeSize / 2 - point.x * safeZoom}px, ${loupeSize / 2 - point.y * safeZoom}px, 0)`,
            }}
          >
            {renderContent(loupeWidth, loupeHeight, true)}
          </div>
        </div>
      )}
    </div>
  )
}

export default ArtworkLoupe
